import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Datatable from "../../../components/features/Datatable";
import logsService from "../../../services/logsService";
import Filter from "./Filter";
import { EyeIconButton } from "../../../../assets/common-svg/CommonSvg";

const formatDateStr = (dateVal) => {
    if (!dateVal) return "";
    const d = new Date(dateVal);
    if (isNaN(d)) return "";
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const getRowMessage = (row) => row?.disclaimer_message || row?.message || row?.messaege || "";

const truncateWords = (text, wordLimit = 8) => {
    if (!text) return "-";
    const plainText = String(text).replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    if (!plainText) return "-";
    const words = plainText.split(" ");
    if (words.length <= wordLimit) return plainText;
    return `${words.slice(0, wordLimit).join(" ")}...`;
};

const filterConfig = {
    role_id: {
        label: "Role",
    },
    date_range: {
        label: "Date Range",
    }
};

export default function LogsManagement() {
    const navigate = useNavigate();
    const [logsData, setLogsData] = useState({
        items: [],
        total_count: 0,
        page: 1,
        per_page: 10,
    });
    const [loading, setLoading] = useState(false);
    const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
    const [selectedMessageHtml, setSelectedMessageHtml] = useState("");

    const openMessageModal = (messageHtml) => {
        if (!messageHtml) return;
        setSelectedMessageHtml(messageHtml);
        setIsMessageModalOpen(true);
    };

    const handleServerSideChange = async (params) => {
        // Extract dates
        let start_date = "";
        let end_date = "";
        let role_id = params.role_id || "";

        if (params.date_range) {
            const dr = params.date_range;
            if (dr.from || dr.to) {
                // Keep the CustomDateRangePicker DD/MM/YYYY text unmodified
                start_date = dr.from || "";
                end_date = dr.to || "";
            } else if (Array.isArray(dr)) {
                if (dr[0] && dr[0].startDate) {
                    start_date = dr[0].startDate ? formatDateStr(dr[0].startDate) : "";
                    end_date = dr[0].endDate ? formatDateStr(dr[0].endDate) : "";
                } else {
                    start_date = dr[0] ? formatDateStr(dr[0]) : "";
                    end_date = dr[1] ? formatDateStr(dr[1]) : "";
                }
            }
        }

        try {
            setLoading(true);

            // Construct payload to match expected POST format
            const payload = {
                filter: {
                    search: (params.search || "").trim(),
                    role_id: role_id,
                    start_date: start_date,
                    end_date: end_date
                }
            };

            const response = await logsService.getAllLogs(
                params.page || 1,
                params.per_page || 10,
                payload
            );

            const dataItems = response?.data?.logs || [];
            const pagination = response?.data?.pagination || {};

            setLogsData({
                items: dataItems,
                total_count: pagination.total_records || 0,
                page: pagination.current_page || params.page,
                per_page: pagination.limit || params.per_page,
            });

        } catch (error) {
            console.error("Error fetching logs list:", error);
            setLogsData((prev) => ({ ...prev, items: [], total_count: 0 }));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        handleServerSideChange({ page: 1, per_page: 10, search: "" });
    }, []);

    const columns = [
        {
            id: "name",
            name: "Name",
            align: "center",
            selector: (row) => row.name || '-',
            sortable: true,
        },
        {
            id: "email",
            name: "Email",
            align: "center",
            selector: (row) => (
                <Tooltip title={row.email || "-"} placement="top">
                    <span className="inline-block max-w-[220px] truncate align-middle">
                        {row.email || "-"}
                    </span>
                </Tooltip>
            ),
            sortable: true,
        },
        {
            id: "role_title",
            name: "Role",
            align: "center",
            selector: (row) => row.role_title || '-',
            sortable: true,
        },
        {
            id: "title",
            name: "Title",
            align: "center",
            selector: (row) => (
                <Tooltip title={row.title || "-"} placement="top">
                    <span className="inline-block max-w-[220px] truncate align-middle">
                        {row.title || "-"}
                    </span>
                </Tooltip>
            ),
            sortable: true,
        },
        {
            id: "message",
            name: "Message",
            align: "center",
            selector: (row) => {
                const messageHtml = getRowMessage(row);
                const messagePreview = truncateWords(messageHtml, 8);

                if (!messageHtml) {
                    return "-";
                }

                return (
                    <Tooltip title="Click to view full message" placement="top">
                        <button
                            type="button"
                            onClick={() => openMessageModal(messageHtml)}
                            className="inline-block max-w-[220px] truncate align-middle text-[#3571FF] underline hover:opacity-80 text-left"
                        >
                            {messagePreview}
                        </button>
                    </Tooltip>
                );
            },
            sortable: true,
        },
        {
            id: "ip_address",
            name: "IP Address",
            align: "center",
            selector: (row) => row.ip_address || "-",
            sortable: true,
        },
        {
            id: "date",
            name: "Date",
            align: "center",
            selector: (row) => row.date_time?.date || row.date || "-",
            sortable: true,
        },
        {
            id: "action",
            name: "Action",
            align: "center",
            selector: (row) => (
                <Box className="flex justify-center">
                    <Tooltip title="View">
                        <IconButton
                            onClick={() =>
                                navigate(`view/${row.id}`, { state: { row } })
                            }
                        >
                            <EyeIconButton bgColor={"#3571FF"} />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
            sortable: false,
        },
    ];

    return (
        <div className="!p-6">
            <div className="!p-4 mt-3 bg-white flex flex-col gap-4">
                <Datatable
                    columns={columns}
                    data={{
                        items: logsData.items,
                        total_count: logsData.total_count,
                    }}
                    title="Disclaimer Logs"
                    searchPlaceholder="Search by Name or Email..."
                    loading={loading}
                    remote={true}
                    onServerSideChange={handleServerSideChange}
                    FilterComponent={Filter}
                    filterConfig={filterConfig}
                />
            </div>

            <Dialog
                open={isMessageModalOpen}
                onClose={() => setIsMessageModalOpen(false)}
                fullWidth
                maxWidth="xl"
                PaperProps={{
                    sx: { width: "85vw", maxWidth: "1305px",margin:"5px" },
                }}
            >
                <DialogTitle className="flex items-center justify-between">
                    <span>Disclaimer Message</span>
                    <IconButton
                        aria-label="close"
                        onClick={() => setIsMessageModalOpen(false)}
                        size="small"
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <div
                        className="prose max-w-none"
                        dangerouslySetInnerHTML={{ __html: selectedMessageHtml }}
                    />
                </DialogContent>
            </Dialog>
        </div>
    );
}